import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { FormWrapper } from '@/form-control'
import ROUTES from '@/ROUTES'

import { IUser } from '@/MODELS/user.model'

import analytics from '@/services/analytics.service'
import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'

import {
    ChildInfoSection,
    CommunicationLevelSection,
    DiagnosisInfoSection,
    FinalConfirmationSection,
    JourneyStageSection,
    MainConcernsSection,
    ServicesReceivedSection,
    SupportNeedsSection,
    UserGoalsSection,
    UserGoalsSectionCaregivers,
    WelcomeSection,
} from '@/components/OnboardingSteps'

import logo from '../../logo-white-no-bg.svg'

export default function Onboarding(props: { id?: string }) {
    const { user, fetchUser, checkAuth } = useAuth()
    const navigate = useNavigate()

    const onboardingStartTime = useRef<number>(Date.now())
    const [showCelebration, setShowCelebration] = useState(false)
    // A sponsored family whose org requires provider review doesn't get a roadmap
    // generated for them on the spot — the provider prepares it. So we skip the AI
    // "worth the wait" loading screen and frame completion as provider-led.
    const [reviewRequired, setReviewRequired] = useState(false)

    const params = useParams()
    const id = props?.id ?? params?.id ?? 'new'

    // Ensure auth session is established (restores token from refresh cookie on page reload)
    useEffect(() => {
        checkAuth()
    }, [])

    // Learn whether this family's roadmap will be provider-reviewed, so the submit
    // and completion screens can drop the AI-generation framing.
    useEffect(() => {
        let cancelled = false
        api<{ roadmapReviewRequired?: boolean }>(ROUTES.app.sponsorship).then((res) => {
            if (!cancelled && res.data?.roadmapReviewRequired) setReviewRequired(true)
        })
        return () => {
            cancelled = true
        }
    }, [])

    // Only auto-navigate if onboarding was already complete (e.g., page reload) — not after fresh completion
    useEffect(() => {
        if (user?.onboardingComplete && !showCelebration) {
            navigate('/roadmap')
        }
    }, [user, navigate])

    return (
        <div className='w-full bg-gradient-to-br from-cyan-600 via-cyan-500 to-cyan-600'>
            <div className='relative flex flex-col min-h-dvh overflow-y-scroll max-w-7xl w-full mx-auto pt-[env(safe-area-inset-top)]'>
                <header className='relative z-10 flex-shrink-0 px-4 py-8 mb-5'>
                    <div className='text-center'>
                        <img
                            src={logo}
                            className='absolute left-0 top-0 h-12 mx-auto sm:h-16 lg:h-20'
                            alt='BrightPath'
                        />
                    </div>
                </header>

                <div className='relative z-10 flex items-start justify-center mt-9 flex-1 px-4 pb-8'>
                    <FormWrapper<IUser>
                        endpoint={ROUTES.app.completeOnboarding}
                        id={null}
                        multipart
                        preventInitialLoad
                        persistKey='brightpath-onboarding'
                        insertIntoPostBody={{ onboardingComplete: true }}
                        // Onboarding triggers OpenAI roadmap generation which can take 30-40s under load.
                        // 2 minutes gives the backend time to try gpt-4.1 (60s timeout), and if that fails,
                        // retry with the faster gpt-4.1-nano — all within the window users are told to expect.
                        requestTimeout={120_000}
                        className='w-full max-w-2xl contrast-form'
                        submitButtonClass={
                            'bg-white text-cyan-600 hover:bg-white/90 border-0 shadow-lg hover:shadow-xl transition-all duration-200 font-semibold py-3 px-6 rounded-xl text-lg w-full'
                        }
                        errorMessages={{
                            500: "We're experiencing a temporary issue setting up your account. Our team has been notified and is working to resolve it. Thank you for your patience!",
                        }}
                        hideSubmissionRowPending={true}
                        redirectAfterSubmit={false}
                        callbackAfterSubmit={async () => {
                            // Show celebration immediately — before any await so it batches
                            // with the FormWrapper success state in a single render
                            setShowCelebration(true)
                            const completionTime = Math.round((Date.now() - onboardingStartTime.current) / 1000)
                            analytics.trackOnboardingComplete(7, completionTime)
                            await fetchUser()
                        }}
                    >
                        {(f, { formValues, setFormValues, submissionStatus }) => {
                            const sections = []

                            sections.push(
                                <WelcomeSection
                                    key='step-1'
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    f={f}
                                />
                            )

                            sections.push(
                                <ChildInfoSection
                                    key='step-2'
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    f={f}
                                />
                            )

                            sections.push(
                                <MainConcernsSection
                                    key='step-3'
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    f={f}
                                />
                            )

                            sections.push(
                                <JourneyStageSection
                                    key='step-4'
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    f={f}
                                />
                            )

                            if (
                                formValues?.userInfo?.currentDiagnoseStep === 'diagnosed recently' ||
                                formValues?.userInfo?.currentDiagnoseStep === 'diagnosed over a year ago'
                            ) {
                                sections.push(
                                    <DiagnosisInfoSection
                                        key='step-5'
                                        formValues={formValues}
                                        setFormValues={setFormValues}
                                        f={f}
                                    />
                                )
                            }

                            sections.push(
                                <ServicesReceivedSection
                                    key='step-services-received'
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    f={f}
                                />
                            )

                            sections.push(
                                <CommunicationLevelSection
                                    key='step-communication'
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    f={f}
                                />
                            )

                            sections.push(
                                <SupportNeedsSection
                                    key='step-support-needs'
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    f={f}
                                />
                            )

                            sections.push(
                                <UserGoalsSection
                                    key='step-6'
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    f={f}
                                />
                            )

                            sections.push(
                                <UserGoalsSectionCaregivers
                                    key='step-7'
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    f={f}
                                />
                            )

                            sections.push(
                                <FinalConfirmationSection
                                    key='final-step'
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    f={f}
                                    submissionStatus={submissionStatus}
                                    user={user}
                                    reviewRequired={reviewRequired}
                                />
                            )

                            return sections
                        }}
                    </FormWrapper>
                </div>
            </div>
            {/* Celebration screen — shown after onboarding completes, before navigating to roadmap */}
            {showCelebration && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-cyan-600 via-cyan-500 to-cyan-600'>
                    <div className='w-full max-w-sm mx-4 text-center animate-in fade-in slide-in-from-bottom-4 duration-700'>
                        <div className='text-6xl mb-6'>
                            {reviewRequired ? '✨' : '🎉'}
                        </div>
                        {reviewRequired ? (
                            <>
                                <h2 className='text-3xl font-bold text-white mb-3 drop-shadow-lg'>
                                    You're all set!
                                </h2>
                                <p className='text-lg text-white/90 mb-2 leading-relaxed'>
                                    Your provider is putting together your personalized roadmap based on everything you shared.
                                </p>
                                <p className='text-base text-white/80 mb-8'>
                                    You'll see it here as soon as it's ready. In the meantime, explore the app and your resources.
                                </p>
                            </>
                        ) : (
                            <>
                                <h2 className='text-3xl font-bold text-white mb-3 drop-shadow-lg'>
                                    Your Roadmap is Ready!
                                </h2>
                                <p className='text-lg text-white/90 mb-2 leading-relaxed'>
                                    We've built a personalized journey just for your family based on everything you shared.
                                </p>
                                <p className='text-base text-white/80 mb-8'>
                                    Start with your first milestone and check off each step as you go. Small steps lead to big progress!
                                </p>
                            </>
                        )}
                        <button
                            onClick={async () => {
                                // Ensure user context is up to date before navigating, but
                                // also pass `fromCelebration` so OnboardingWrapper lets us
                                // through even if the React state commit for the post-submit
                                // setUser hasn't flushed by the time the route change fires.
                                // Without this, a stale `onboardingComplete: false` in the
                                // auth context bounces us back to /onboarding step 1 briefly.
                                if (!user?.onboardingComplete) await fetchUser()
                                navigate('/roadmap', { state: { fromCelebration: true } })
                            }}
                            className='w-full px-8 py-4 text-lg font-bold text-cyan-700 bg-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200'
                        >
                            Let's Go!
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
